<template>
    <loading v-if="loading || isLoadingProgress"></loading>
    <div class="students-overview" v-else>

        <header class="page-header page-header--students">
            <a href="#" @click.prevent="showGroupSettings(group)" class="tag button-timid left button--show-planning print-hide"><span class="icon icon--inline icon--settings-dark"></span> Planning aanpassen</a>
            <h1>klas {{ group.name }}</h1>

            <hr>
        </header>

        <div class="page-content print-hide">
          <term-nav :terms="terms" :currentGroupTermId="currentGroupTerm.id" :selectedGroupTermId="groupTerm.id" />
          <empty-group-message :group="group" v-if="students.length === 0 && !group.archived"/>
        </div>

        <div class="progress-overview">

            <table class="table-header-rotated">

                <thead class="table__header--rotated">
                    <tr>
                        <th class="table__col--first"></th>
                        <th class="table__col-header table__col-header--rotated" v-for="assignment in assignmentCols">
                            <div class="table__col-header-wrap">
                                <router-link :to="{ name: 'assignment.group', params: { periodId: assignment.periodId, assignmentId: assignment.id }  }" class="table__col-header-inner table__link tag" :class="{ 'period-end': assignment.lastInPeriod }">{{ assignment.title }}</router-link>
                            </div>
                        </th>
                    </tr>
                </thead>

                <tbody>
                    <tr class="table__row" v-for="student in studentProgress">

                        <th class="table__row-header">

                            <router-link :to="{ name: 'students.period', params: { periodId: currentGroupPeriod.id, studentId: student.id }  }" class="table__header-inner table__link tag">{{ student.name }}</router-link>

                        </th>
                        <td v-for="(assignment, i) in assignmentCols" :class="{ 'cell--period-end': assignment.lastInPeriod, 'cell--important': student.progressions[i].isImportant }">
                            <a href="#"
                                @click.prevent="viewProgress(student.progressions[i])"
                                :title="translation(student.progressions[i].tag)"
                                class="table__cell-wrap"
                                :class="'table__cell-wrap--'+student.progressions[i].tag"
                                >

                                <div class="progress__icon" :class="'progress__icon--'+student.progressions[i].tag" >
                                    <img :src="spriteImage" class="inline-icon" draggable="false" />
                                </div>

                                <span v-if="student.progressions[i].isRetaking || student.progressions[i].hasNew"
                                    class="progress__attention-icon print-hide"
                                    :class="{
                                        'progress__attention-icon--retaking': student.progressions[i].isRetaking,
                                        'progress__attention-icon--retaking-done': student.progressions[i].isRetakingDone,
                                        }"></span>

                            </a>
                        </td>

                    </tr>

                    <!-- empty filler if no students -->
                    <template v-if="students.length === 0">
                    <tr class="table__row" v-for="i in 3">
                        <th class="table__row-header">
                          <span class="table__header-inner table__link tag">Voorbeeld leerling {{ i }}</span>
                        </th>
                        <td v-for="(assignment, i) in assignmentCols" :class="{ 'cell--period-end': assignment.lastInPeriod }">
                          <div class="table__cell-wrap"><div class="progress__icon" ></div></div>
                        </td>
                    </tr>
                  </template>

                </tbody>

            </table>

        </div>

        <template v-if="students.length">

            <a href="#" @click.prevent="showAggregate" class="tag button-timid button--show-aggregate print-hide"><span class="icon icon--inline icon--print-dark"></span> Voortgang en beoordelingen klas {{ group.name }} afdrukken <loading :mini="true" :inline="true" v-if="aggregateVisible"></loading></a>
            <aggregate v-if="aggregateVisible" :group="group" :students="students" :assignments="assignments" @load="printPage" class="aggregate--print-only"></aggregate>

        </template>

    </div>
</template>


<script>

import { getCurrentGroupTermForGroup, getAssignmentsForGroupPeriod, getCurrentGroupPeriodForGroupTerm } from 'js/vuex/getters'
import Loading from 'js/components/elements/Loading.vue'
import { username } from 'js/helpers/transformers'
import api from 'js/api/resources'
import { transform } from 'js/vuex/helpers'
import track from 'js/helpers/analytics'
import sprite from 'js/helpers/sprite'

import EmptyGroupMessage from 'js/components/elements/EmptyGroupMessage.vue'
import TermNav from 'js/components/elements/TermNav.vue'
import Aggregate from './Aggregate.vue'


export default {
    name: 'Students',
    props: {
        group: {},
        loading: {
            type: Boolean,
            default: false,
        },
        groupTermId: {
            required: false,
        },
    },
    components: { Loading, Aggregate, EmptyGroupMessage, TermNav },

    data: function() {
        return {
            isLoadingProgress: true,
            aggregateVisible: false,
        }
    },

    vuex: {
        getters: {
            getCurrentGroupTermForGroup,
            getAssignmentsForGroupPeriod,
            getCurrentGroupPeriodForGroupTerm,
        },
        actions: {
            loadProgress: ({dispatch}, group) => {
                return api.getProgressForGroup(group.id)
                    .then( response => transform(response.body) )
                    .then( result => {
                        dispatch('ADD_PROGRESS', result.progress || [])
                        dispatch('ADD_DILIGENCE', result.diligence || [])
                        dispatch('ADD_BADGES', result.badges || [])
                    })
            },
            showGroupSettings: ({dispatch}, group) => {
                dispatch('SHOW_MODAL', 'GroupSettings', { group: group, landingPage: 'Planning' })
            },
        }
    },

    computed: {
        students: function() {
            return this.group.students.map( id => this.$store.state.users.storage[id] )
        },
        studentProgress: function() {
            const assignments = this.assignments
            return this.students
                .map( u => {
                    return {
                        id: u.id,
                        name: username(u),
                        progressions: assignments.map( a => {
                            const progress = this.progressForStudentAndAssignment(u, a)
                            return {
                                assignmentId: a.id,
                                studentId: u.id,
                                isRetaking: progress && progress.isRetaking,
                                isRetakingDone: progress && progress.finished != null && !progress.isReviewed && progress.diligence != null,
                                hasNew: progress && progress.finished != null && !progress.isReviewed,
                                canView: progress && progress.finished != null,
                                tag: this.tagForProgress(progress, a),
                                isImportant: progress && progress.badge != null
                            }
                        })
                    }
                })
        },
        periodAssignments: function() {
            const filterOptionalAssignments = true
            return this.groupTerm.groupPeriods.map( id => this.getAssignmentsForGroupPeriod(id, filterOptionalAssignments) )
        },
        assignments: function() {
            return this.periodAssignments.reduce( (list, assignments) => list.concat(assignments) )
        },
        assignmentCols: function() {
            return this.periodAssignments.reduce( (list, assignments, pIdx) => {

                return list.concat(
                    assignments.map( (a, idx) => ({
                        id: a.id,
                        title: a.title,
                        lastInPeriod: idx === assignments.length - 1,
                        periodId: this.groupTerm.groupPeriods[pIdx],
                    }))
                )

            }, [])
        },
        progressions: function() {
            return Object.keys(this.$store.state.progress.storage)
                .map( id => this.$store.state.progress.storage[id] )
                .filter( progress => progress.group == this.group.id)
        },
        currentGroupTerm: function() {
            return this.getCurrentGroupTermForGroup(this.group.id)
        },
        groupTerm: function() {
            if ( this.groupTermId == null) {
                return this.currentGroupTerm
            }
            return this.$store.state.groups.terms[this.groupTermId]
        },
        currentGroupPeriod: function() {
            return this.getCurrentGroupPeriodForGroupTerm(this.currentGroupTerm)
        },
        previousGroupPeriods: function() {

            return this.groupTerm.groupPeriods
                .map( id => this.$store.state.groups.periods[id])
                .filter( el => el.finished || el.end < this.currentGroupPeriod.end )

        },
        overdueAssignmentIds: function() {
            return this.previousGroupPeriods
                .reduce( (assignments, gp) => assignments.concat( gp.groupAssignments ), [])
                .map(id => this.$store.state.groups.assignments[id].assignment[0] )
        },

        spriteImage: function() {
            return sprite('sprite-main@2x.png')
        },

        term: function() {
            return this.$store.state.terms.storage[ this.groupTerm.term[0] ]
        },

        terms: function() {
            return this.group.groupTerms
              .map(id => this.$store.state.groups.terms[id])
              .filter(gt => gt.term != null && gt.term.length > 0)
              .map(gt => {
                  const term = this.$store.state.terms.storage[gt.term[0]]
                  return {
                      id: term.id,
                      title: term.title,
                      groupTermId: gt.id
                  }
              })

        }

    },

    methods: {
        username,
        progressForStudentAndAssignment: function(student, assignment) {
            return this.progressions.find( progress => progress.user == student.id && progress.assignment == assignment.id )
        },
        tagForProgress: function(progress, assignment) {
            if ( progress == null || progress.finished == null ) {
                return this.isOverdue(assignment) ? 'overdue' : 'missing'
            }

            if (progress.badge != null) {
                return this.$store.state.badges.storage[progress.badge[0]].type
            }

            if (progress.diligence == null ) {
                return 'done'
            }

            return this.$store.state.diligence.storage[progress.diligence[0]].tag
        },

        isOverdue: function(assignment) {
            return this.overdueAssignmentIds.indexOf(assignment.id) !== -1
        },

        viewProgress: function(progression) {
            if ( progression.canView ) {
                this.$router.push(
                    { name: 'students.assignment.overview', params: { assignmentId: progression.assignmentId, studentId: progression.studentId }}
                )
            }

        },

        showAggregate: function() {
            this.aggregateVisible = true
        },

        printPage: function() {
            track.event('group', 'print', this.group.id)

            const prevTitle = window.document.title
            window.document.title = 'Kiempunt overzicht klas ' + this.group.name

            this.$nextTick( () => {

                this.afterPrint( () => {
                    this.aggregateVisible = false
                    window.document.title = prevTitle
                })
                window.print()

            })
        },

        afterPrint: function(cb) {

            const mediaQueryList = window.matchMedia('print');
            const after = (mql) => {
                if (!mql.matches) {
                    cb()
                    mediaQueryList.removeListener(after)
                }
            }

            mediaQueryList.addListener(after)

        },

        loadData: function() {

            if ( this.groupTerm == null || this.group.groupTerms.indexOf( this.groupTerm.id ) === -1 ) {
                this.$router.push({ name: 'students.overview', params: { groupTermId: this.currentGroupTerm.id }})
                return
            }

            this.isLoadingProgress = true

            this.loadProgress(this.group)
                .catch( err => track.exception('Could not load students progress', err) )
                .then( () => this.isLoadingProgress = false )
        },

        translation (tag) {
            const map = {
                'sufficient': 'Goede inzet',
                'ok': 'Voldoende inzet',
                'fail': 'Onvoldoende inzet',
                'done': 'Klaar, nog niet beoordeeld',

                'idea': 'Bekroning ideevorming',
                'design': 'Bekroning vormgeving',
                'technique': 'Bekroning materiaal & techniek',
                'artappreciation': 'Bekroning kunstbeschouwing',
                'reflection': 'Bekroning reflectie',
            }

            return map[tag] || tag
        },



    },

    created: function() {
        this.loadData()
    },

}
</script>


<style lang="scss">

    @import 'global';

    .progress-overview {
        // margin: 0 -6.8%;
        overflow: auto;
    }

    .table-header-rotated {

        table-layout: fixed; // all columns same width
        width: 92%; // makes sure right rotated headers are still visible
        border-collapse: collapse;
        min-width: 560px;
        margin-bottom: 2em;

        td {
            padding: 0;
            text-align: center;
            border: 1px solid $color-light-grey;

            .progress__icon {
                margin: 0 auto;
            }

            &.cell--period-end {
                border-right-color: $color-grey;
            }
            &.cell--important {
            }
        }

        th.table__col-header--rotated {
            height: 140px;
            white-space: nowrap;
            vertical-align: bottom;

            // Firefox needs the extra DIV for some reason, otherwise the text disappears if you rotate
            .table__col-header-wrap {
                transform: translateX(100%) rotate(-45deg);
                transform-origin: bottom left;
                text-align: left;

                .table__col-header-inner {
                    border-bottom: 1px solid $color-light-grey;
                    padding: 5px 10px;
                    &.period-end { }
                }

            }

        }

        .table__cell-wrap {
            padding: 0.6em 15%;
            display: flex;
            justify-content: center;
            position: relative; // used for retake icon
            -webkit-tap-highlight-color: rgba(0,0,0,0);

            .progress__icon {
                flex-shrink: 0;
            }

            &:hover {
                background-color: $color-lightest-grey;
            }
            &:active {
                background-color: $color-light-grey;
            }


            &--missing {
                visibility: hidden;
            }

            &--overdue {
                cursor: default;
            }

        }

        .table__col--first {
            width: 9em;
        }

        th.table__row-header {
            padding: 0 10px;
            border-top: 1px solid $color-light-grey;
            border-bottom: 1px solid $color-light-grey;
            text-align: left;
            line-height: 1.1em;
        }

        @media only screen and (max-width: 1140px), print {

            .table__col--first {
                width: 120px;
            }

            .progress__icon {
                transform: scale(0.6);
            }
            .progress__attention-icon {
                transform: scale(0.7);
                left: 50%;
                bottom: 50%;
            }

        }

    }

    .progress__icon {
        display: block;

        // needed for inline-sprite
        position: relative;
        overflow: hidden;

        @include sprite-width($rating-icon-neutral);
        @include sprite-height($rating-icon-neutral);

        .inline-icon {
            @include sprite-inline($rating-icon-neutral);
            margin: 0;
        }

        @each $type, $value in $progress-types {
            &--#{$type} {
                .inline-icon {
                    @include sprite-inline-position($value);
                }
            }
        }

    }

    .progress__attention-icon {

        @include background-sprite-replace($progress-icon-attention-group);
        position: absolute;
        left: 55%;
        bottom: 55%;
        pointer-events: none;

        &--retaking {
            @include sprite-position($progress-icon-attention-retake);
        }

    }

    .aggregate--print-only {
        display: none;
    }

    @media print {

        .page-header--students {
            margin-bottom: 0;
        }

        .aggregate--print-only {
            display: block;
        }

        .print-hide {
            display: none !important;
        }

        .students-overview {
            h1 {
                margin: 0;
            }
        }

        .progress-overview {
            margin: 0;
            overflow: visible;

            a {
                text-decoration: none;
            }

        }

        .table-header-rotated {

            .table__header--rotated {
                // don't repeat on every page
                display: table-row-group;
            }

            .table__header-inner, .table__cell-wrap {
                // avoid page break on Safari
                display: inline-block;
                width: 100%;
                vertical-align: top;
            }

            .table__cell-wrap {
                padding: 0;
                text-align: center;
            }
        }


    }


</style>



// WEBPACK FOOTER //
// Students.vue?bf56798a